import { Currency, WNATIVE } from '@pancakeswap/sdk'
import { useMemo, useState } from 'react'
import { useTranslation } from '@pancakeswap/localization'
import tryParseAmount from '@pancakeswap/utils/tryParseAmount'
import { useCustomToast, useTransactionAdder } from 'state/transactions/hooks'
import { useCurrencyBalance } from 'state/wallet/hooks'
import useAccountActiveChain from 'hooks/useAccountActiveChain'
import { SendTransactionResult } from 'wagmi/dist/actions'
import { isUserRejected } from 'utils/sentry'
import { useToast } from '@pancakeswap/uikit'
import { useWNativeContract } from './useContract'
import { useCallWithGasPrice } from './useCallWithGasPrice'

export enum WrapType {
  NOT_APPLICABLE,
  WRAP,
  UNWRAP,
}

const NOT_APPLICABLE = { wrapType: WrapType.NOT_APPLICABLE }
/**
 * Given the selected input and output currency, return a wrap callback
 * @param inputCurrency the selected input currency
 * @param outputCurrency the selected output currency
 * @param typedValue the user input value
 */
export default function useWrapCallback(
  inputCurrency: Currency | undefined,
  outputCurrency: Currency | undefined,
  typedValue: string | undefined,
): { wrapType: WrapType; execute?: undefined | (() => Promise<SendTransactionResult>); inputError?: string } {
  const { t } = useTranslation()
  const { account, chainId } = useAccountActiveChain()
  const { callWithGasPrice } = useCallWithGasPrice()
  const wbnbContract = useWNativeContract()
  const balance = useCurrencyBalance(account ?? undefined, inputCurrency)
  // we can always parse the amount typed as the input currency, since wrapping is 1:1
  const inputAmount = useMemo(() => tryParseAmount(typedValue, inputCurrency), [inputCurrency, typedValue])
  const addTransaction = useTransactionAdder()

  const { toastRejection } = useCustomToast()

  return useMemo(() => {
    if (!wbnbContract || !chainId || !inputCurrency || !outputCurrency) return NOT_APPLICABLE

    const sufficientBalance = inputAmount && balance && !balance.lessThan(inputAmount)

    if (inputAmount && inputCurrency?.isNative && WNATIVE[chainId]?.equals(outputCurrency)) {
      return {
        wrapType: WrapType.WRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
              try {
                const txReceipt = await callWithGasPrice(wbnbContract, 'deposit', undefined, {
                  value: inputAmount.quotient,
                })
                const amount = inputAmount.toSignificant(6)
                const native = inputCurrency.symbol
                const wrap = WNATIVE[chainId].symbol
                addTransaction(txReceipt, {
                  summary: `Wrap ${amount} ${native} to ${wrap}`,
                  translatableSummary: { text: 'Wrap %amount% %native% to %wrap%', data: { amount, native, wrap } },
                  type: 'wrap',
                })
                return txReceipt
              } catch (error) {
                if (isUserRejected(error)) {
                  toastRejection()
                }
                console.error('Could not deposit', error)
                throw error
              }
            }
            : undefined,
        inputError: sufficientBalance
          ? undefined
          : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol }),
      }
    }
    if (inputAmount && WNATIVE[chainId]?.equals(inputCurrency) && outputCurrency?.isNative) {
      return {
        wrapType: WrapType.UNWRAP,
        execute:
          sufficientBalance && inputAmount
            ? async () => {
              try {
                const txReceipt = await callWithGasPrice(wbnbContract, 'withdraw', [inputAmount.quotient])
                const amount = inputAmount.toSignificant(6)
                const wrap = WNATIVE[chainId].symbol
                const native = outputCurrency.symbol
                addTransaction(txReceipt, {
                  summary: `Unwrap ${amount} ${wrap} to ${native}`,
                  translatableSummary: { text: 'Unwrap %amount% %wrap% to %native%', data: { amount, wrap, native } },
                  type: 'unwrap',
                })
                return txReceipt
              } catch (error) {
                if (isUserRejected(error)) {
                  toastRejection()
                }
                console.error('Could not withdraw', error)
                throw error
              }
            }
            : undefined,
        inputError: sufficientBalance
          ? undefined
          : t('Insufficient %symbol% balance', { symbol: inputCurrency.symbol }),
      }
    }
    return NOT_APPLICABLE
  }, [wbnbContract, chainId, inputCurrency, outputCurrency, inputAmount, balance, t, callWithGasPrice, addTransaction, toastRejection])
}

export function useIsWrapping(currencyA: Currency | undefined, currencyB: Currency | undefined, value?: string) {
  const { wrapType } = useWrapCallback(currencyA, currencyB, value)

  return wrapType !== WrapType.NOT_APPLICABLE
}
